import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowRightStartOnRectangleIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import useAuthStore from '../../stores/authStore';
import useStaffProfileStore from '../../stores/staffProfileStore';
import usePermissions from '../../hooks/usePermissions';
import { USER_ROLE } from '../../types/auth';
import { superAdminNav, providerNav, staffNav, NavigationConfig } from '../../config/navigation';

interface SidebarProps {
	isOpen: boolean;
	onClose: () => void;
}

const Sidebar = ({ isOpen, onClose }: SidebarProps) => {
	const navigate = useNavigate();
	const location = useLocation();
	const { user, logout } = useAuthStore();
	const { dashboard: staffDashboard } = useStaffProfileStore();
	const { hasAny } = usePermissions();
	const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set());

	// Get navigation config based on user role
	const getNavigationConfig = (): NavigationConfig => {
		if (!user) return [];

		switch (user.role) {
			case USER_ROLE.SUPER_ADMIN:
				return superAdminNav;
			case USER_ROLE.SALON_OWNER:
				return providerNav;
			case USER_ROLE.STAFF:
				return staffNav;
			default:
				return [];
		}
	};

	const navigationConfig = getNavigationConfig();

	const handleLogout = () => {
		logout();
		navigate('/login');
	};

	const getUserRoleLabel = () => {
		if (!user) return '';
		switch (user.role) {
			case USER_ROLE.SUPER_ADMIN:
				return 'Super Admin';
			case USER_ROLE.SALON_OWNER:
				return 'Salon Owner';
			case USER_ROLE.STAFF:
				return 'Staff';
			default:
				return '';
		}
	};

	const handleNavClick = (path: string) => {
		navigate(path);
		onClose(); // Close sidebar on mobile after navigation
	};

	return (
		<>
			{/* Overlay - Mobile Only */}
			{isOpen && (
				<div
					className="md:hidden fixed inset-0 bg-black/50 z-40 transition-opacity"
					onClick={onClose}
				/>
			)}

			{/* Sidebar */}
			<aside
				className={`
					w-[260px] bg-white dark:bg-gray-800 shadow-[2px_0_10px_rgba(0,0,0,0.05)] dark:shadow-[2px_0_10px_rgba(0,0,0,0.3)]
					flex flex-col flex-shrink-0 transition-all duration-300
					md:relative md:translate-x-0
					fixed top-0 left-0 h-full z-50 md:pt-0
					${isOpen ? 'translate-x-0' : '-translate-x-full'}
				`}
			>
				{/* Sidebar Header */}
				<div className="px-5 py-6 border-b border-gray-200 dark:border-gray-700">
					{/* User name - Mobile only */}
					<div className="md:hidden text-base font-bold text-gray-800 dark:text-gray-100 mb-1">
						{user?.name}
					</div>
					<div className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
						{getUserRoleLabel()}
					</div>
				</div>

				{/* Navigation Menu */}
				<nav className="py-5 flex-1 overflow-y-auto">
					{navigationConfig.map((section, sectionIndex) => (
						<div key={sectionIndex} className="mb-4">
							{section.title && (
								<div className="px-5 pt-4 mb-2 mt-2 border-t border-gray-200 dark:border-gray-700 text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
									{section.title}
								</div>
							)}
							<ul className="list-none m-0 p-0">
								{section.items
									.filter(
										item =>
											user &&
											item.roles.includes(user.role) &&
											(!item.permissions || hasAny(...item.permissions))
									)
									.map(item => {
										const hasChildren =
											item.children && item.children.length > 0;
										const isChildActive =
											hasChildren &&
											item.children!.some(
												child =>
													location.pathname === child.path ||
													location.pathname.startsWith(child.path + '/')
											);
										const isActive =
											!hasChildren &&
											(location.pathname === item.path ||
												location.pathname.startsWith(item.path + '/'));
										const isExpanded =
											expandedMenus.has(item.path) || isChildActive;

										const toggleExpand = () => {
											setExpandedMenus(prev => {
												const next = new Set(prev);
												if (next.has(item.path)) next.delete(item.path);
												else next.add(item.path);
												return next;
											});
										};

										if (hasChildren) {
											return (
												<li key={item.path} className="mb-0.5">
													<button
														onClick={toggleExpand}
														className={`
															w-full flex items-center gap-3 px-5 py-3 text-left cursor-pointer
															border-l-[3px] font-medium transition-all duration-300
															${
																isChildActive
																	? 'text-[#667eea] border-l-[#667eea] bg-indigo-50/50 dark:bg-indigo-900/10'
																	: 'text-gray-600 dark:text-gray-300 border-l-transparent hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-[#667eea]'
															}
														`}
													>
														<item.icon className="w-6 h-6" />
														<span>{item.label}</span>
														<ChevronDownIcon
															className={`w-4 h-4 ml-auto transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''} ${isChildActive ? 'text-[#667eea]' : 'text-gray-400'}`}
														/>
													</button>
													<ul
														className="list-none m-0 p-0 overflow-hidden transition-all duration-300"
														style={{
															maxHeight: isExpanded
																? `${item.children!.length * 44}px`
																: '0px',
														}}
													>
														{item.children!.map(child => {
															const isChildItemActive =
																location.pathname === child.path ||
																location.pathname.startsWith(
																	child.path + '/'
																);
															return (
																<li key={child.path}>
																	<button
																		onClick={() =>
																			handleNavClick(
																				child.path
																			)
																		}
																		className={`
																			w-full flex items-center gap-2 pl-14 pr-5 py-2.5 text-left cursor-pointer
																			text-sm font-medium transition-all duration-200
																			${
																				isChildItemActive
																					? 'text-[#667eea] font-semibold bg-indigo-50/50 dark:bg-indigo-900/10 border-l-[3px] border-l-[#667eea] pl-[53px]'
																					: 'text-gray-500 dark:text-gray-400 border-l-[3px] border-l-transparent hover:text-[#667eea] hover:bg-gray-50 dark:hover:bg-gray-700'
																			}
																		`}
																	>
																		<span
																			className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isChildItemActive ? 'bg-[#667eea]' : 'bg-gray-300 dark:bg-gray-600'}`}
																		/>
																		<span>{child.label}</span>
																	</button>
																</li>
															);
														})}
													</ul>
												</li>
											);
										}

										return (
											<li key={item.path} className="mb-0.5">
												<button
													onClick={() => handleNavClick(item.path)}
													className={`
														w-full flex items-center gap-3 px-5 py-3 text-left cursor-pointer
														border-l-[3px] font-medium transition-all duration-300
														${
															isActive
																? 'bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 text-[#667eea] border-l-[#667eea] font-semibold'
																: 'text-gray-600 dark:text-gray-300 border-l-transparent hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-[#667eea] hover:border-l-[#667eea]'
														}
													`}
												>
													<item.icon className="w-6 h-6" />
													<span>{item.label}</span>
													{user?.role === USER_ROLE.STAFF &&
														item.label === 'Bookings' &&
														staffDashboard &&
														staffDashboard.todayAppointmentCount >
															0 && (
															<span className="ml-auto bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
																{
																	staffDashboard.todayAppointmentCount
																}
															</span>
														)}
												</button>
											</li>
										);
									})}
							</ul>
						</div>
					))}
				</nav>

				{/* Logout Button - Always visible at bottom */}
				<div className="py-4 px-4 border-t border-gray-200 dark:border-gray-700 mt-auto">
					<button
						onClick={handleLogout}
						className="w-full flex items-center gap-3 px-4 py-3 text-left font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all cursor-pointer"
					>
						<ArrowRightStartOnRectangleIcon className="w-6 h-6" />
						<span>Logout</span>
					</button>
				</div>
			</aside>
		</>
	);
};

export default Sidebar;
